import React, { useState, useEffect } from "react";

// @material-ui/core components
import { useGlobal } from "GlobalContext";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";

// @material-ui/icons
import PermIdentity from "@material-ui/icons/PermIdentity";

// core components
import { useHistory } from "react-router-dom";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import CoreButton from "@material-ui/core/Button";
import CustomInput from "components/CustomInput/CustomInput.js";
import Clearfix from "components/Clearfix/Clearfix.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardAvatar from "components/Card/CardAvatar.js";

import styles from "assets/jss/material-dashboard-pro-react/views/editProfileStyles.js";
import avatar from "assets/img/noimage.png";

import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API;
const useStyles = makeStyles(styles);

/**
 * Vista de edición de perfil.
 *
 * Objetivos de esta versión:
 * - eliminar widths inline y marginRight rígidos
 * - apilar correctamente en móvil usando GridItem xs/sm/md
 * - corregir el bug de redirección/limpieza en caso de error
 * - hacer los diálogos cómodos en móvil
 */
export default function EditProfile() {

  useEffect(() => {
    if (!sharedValue) return;

    setForm({
      nombre: sharedValue.nombre || "",
      username: sharedValue.username || "",
      email: sharedValue.email || "",
      telefono: sharedValue.telefono || "",
      biografia: sharedValue.biografia || "",
      institution: sharedValue.institution || "",
    });
  }, [sharedValue]);

  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const { sharedValue, updateSharedValues, clearUserData } = useGlobal();
  const {
    username,
    nombre,
    email,
    rol,
    telefono,
    biografia,
    institution,
    id,
    fotoPerfil,
  } = sharedValue || {};

  const { t } = useTranslation("editProfile");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isError, setError] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Solo dejamos editables los campos que ya manejabas:
   * - nombre
   * - biografia
   *
   * El resto se muestra como lectura.
   */
  const [form, setForm] = useState({
    nombre: nombre || "",
    username: username || "",
    email: email || "",
    telefono: telefono || "",
    biografia: biografia || "",
    institution: institution || "",
  });

  /**
   * Validación local.
   *
   * Nota:
   * En tu código anterior había una comprobación de teléfono con:
   *   form.telefono ==! ""
   * que era incorrecta.
   *
   * Como ahora el teléfono no se edita en esta pantalla, no lo validamos aquí.
   */
  const validateForm = () => {
    if (!form.nombre || form.nombre.trim() === "") {
      setError(t("editprofile-error-name-empty"));
      return false;
    }

    setError(null);
    return true;
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  /**
   * Guarda cambios.
   *
   * Corrección importante:
   * antes el finally hacía history.push("/profile")
   * aunque el guardado hubiese fallado.
   *
   * Ahora solo navegamos si la edición fue correcta.
   */
  const editRequest = async () => {
    if (isSaving) return;
    if (!validateForm()) return;

    setIsSaving(true);
    setShowStatus(false);
    setError(null);

    try {
      const requestBody = {
        nombre: form.nombre.trim(),
        telefono: form.telefono,
        biografia: form.biografia,
        institution: form.institution,
      };

      const editResp = await fetch(`/api/usuarios/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const response = await editResp.json();

      if (response.ok !== true) {
        setError(response.mensaje);
        return;
      }

      updateSharedValues({
        nombre: form.nombre.trim(),
        email: form.email,
        username: form.username,
        telefono: form.telefono,
        biografia: form.biografia,
        institution: form.institution,
      });

      setShowStatus(true);
      setConfirmOpen(false);
      history.push("/profile");
      setIsSaving(false);
      return;
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  /**
   * Borra cuenta.
   *
   * Corrección importante:
   * antes el finally limpiaba usuario y redirigía incluso si el DELETE fallaba.
   *
   * Ahora eso solo ocurre si la respuesta es correcta.
   */
  const deleteRequest = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    setError(null);

    try {
      const deleteResp = await fetch(`/api/usuarios/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      const response = await deleteResp.json();

      if (response.ok !== true) {
        setError(response.mensaje);
        return;
      }

      setDeleteOpen(false);
      clearUserData();
      history.push("/");
      window.location.reload();
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      {/* Diálogo de confirmación de guardado */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="editprofile-confirm-dialog-title"
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle id="editprofile-confirm-dialog-title">
          {t("editprofile-confirm-dialog-title")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("editprofile-confirm-dialog-text")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <CoreButton onClick={() => setConfirmOpen(false)} color="default">
            {t("editprofile-confirm-dialog-back")}
          </CoreButton>
          <CoreButton color="secondary" onClick={editRequest} disabled={isSaving}>
            {t("editprofile-confirm-dialog-confirm")}
          </CoreButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo de borrado */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        aria-labelledby="editprofile-delete-dialog-title"
        fullWidth
        maxWidth="xs"
        fullScreen={fullScreenDialog}
      >
        <DialogTitle id="editprofile-delete-dialog-title">
          {t("editprofile-delete-dialog-title")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("editprofile-delete-dialog-text")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <CoreButton onClick={() => setDeleteOpen(false)} color="default">
            {t("editprofile-delete-dialog-cancel")}
          </CoreButton>
          <CoreButton color="secondary" onClick={deleteRequest} disabled={isDeleting}>
            {t("editprofile-delete-dialog-confirm")}
          </CoreButton>
        </DialogActions>
      </Dialog>

      <GridContainer spacing={3}>
        {/* Columna principal */}
        <GridItem xs={12} sm={12} md={8}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <PermIdentity />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("editprofile-card-title")}
              </h4>
            </CardHeader>

            <CardBody>
              {/* Fila 1: editable + lectura */}
              <GridContainer spacing={3}>
                <GridItem xs={12} sm={6}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-fullname")}
                  </Typography>
                  <CustomInput
                    id="nombre"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: form.nombre,
                      onChange: handleChange("nombre"),
                    }}
                  />
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-username", {
                      defaultValue: "Nombre de usuario",
                    })}
                  </Typography>
                  <CustomInput
                    id="username"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: form.username,
                      disabled: true,
                    }}
                  />
                </GridItem>
              </GridContainer>

              {/* Fila 2: solo lectura */}
              <GridContainer spacing={3}>
                <GridItem xs={12} sm={6}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-email", {
                      defaultValue: "Correo electrónico",
                    })}
                  </Typography>
                  <CustomInput
                    id="email"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: form.email,
                      disabled: true,
                    }}
                  />
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-role", { defaultValue: "Rol" })}
                  </Typography>
                  <CustomInput
                    id="rol"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: rol || "",
                      disabled: true,
                    }}
                  />
                </GridItem>
              </GridContainer>

              {/* Fila 3: editable */}
              <GridContainer spacing={3}>
                <GridItem xs={12} sm={6}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-institution", {
                      defaultValue: "Institución",
                    })}
                  </Typography>
                  <CustomInput
                    id="institution"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: form.institution,
                      onChange: handleChange("institution"),
                      // disabled: true,
                    }}
                  />
                </GridItem>

                <GridItem xs={12} sm={6}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-phone", {
                      defaultValue: "Teléfono",
                    })}
                  </Typography>
                  <CustomInput
                    id="telefono"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: form.telefono,
                      disabled: true,
                    }}
                  />
                </GridItem>
              </GridContainer>

              {/* Fila 4: biografía */}
              <GridContainer spacing={3}>
                <GridItem xs={12}>
                  <Typography className={classes.title}>
                    {t("editprofile-label-aboutme")}
                  </Typography>
                  <CustomInput
                    id="about-me"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      multiline: true,
                      rows: 5,
                      value: form.biografia,
                      onChange: handleChange("biografia"),
                    }}
                  />
                </GridItem>
              </GridContainer>

              {/* Estado */}
              {showStatus && !isError ? (
                <Typography
                  style={{ color: "green", marginTop: 12, wordBreak: "break-word" }}
                >
                  {t("editprofile-status-saved")}
                </Typography>
              ) : null}

              {isError ? (
                <Typography
                  style={{ color: "red", marginTop: 12, wordBreak: "break-word" }}
                >
                  {isError}
                </Typography>
              ) : null}

              {/* Acciones */}
              <Box
                mt={2}
                display="flex"
                flexWrap="wrap"
                gridGap={12}
                style={{ gap: 12 }}
              >
                <Button
                  color="primary"
                  className={classes.updateProfileButton}
                  onClick={() => {
                    if (!validateForm()) return;
                    setConfirmOpen(true);
                  }}
                >
                  {t("editprofile-action-update")}
                </Button>

                <Button
                  color="rose"
                  className={classes.updateProfileButton}
                  onClick={() => history.push("/profile")}
                >
                  {t("editprofile-action-cancel-back")}
                </Button>
              </Box>

              <Clearfix />
            </CardBody>
          </Card>
        </GridItem>

        {/* Columna lateral */}
        <GridItem xs={12} sm={12} md={4}>
          <Card profile>
            <CardAvatar profile>
              <img
                src={fotoPerfil || avatar}
                alt={t("editprofile-avatar-alt", { defaultValue: "Avatar" })}
                style={{ objectFit: "cover" }}
              />
            </CardAvatar>

            <CardBody profile>
              <h6 className={classes.cardCategory}>{rol}</h6>
              <h4 className={classes.cardTitle}>{nombre}</h4>
              <p className={classes.description}>
                {biografia || t("editprofile-bio-fallback")}
              </p>

              <Button color="rose" round onClick={() => setDeleteOpen(true)}>
                {t("editprofile-action-delete-account")}
              </Button>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}